import { useEffect, useRef, useState } from 'react';

import { supabase } from '@/integrations/supabase/client';
import { checkEdgeFunctionsStatus } from '@/utils/edgeFunctionHelpers';
import { logger } from '@/utils/logger';
import { showErrorToast } from '@/utils/toast';

// Cache startup check results for the session
let cachedStatus: StartupStatus | null = null;

interface StartupStatus {
  supabaseConnected: boolean;
  edgeFunctionsAvailable: boolean;
  isChecking: boolean;
  errors: string[];
}

/**
 * Hook to check application startup requirements
 * - Supabase connection
 * - Edge Functions availability
 * - Required tables exist
 */
export function useStartupCheck() {
  const [status, setStatus] = useState<StartupStatus>(
    cachedStatus || {
      supabaseConnected: false,
      edgeFunctionsAvailable: false,
      isChecking: true,
      errors: [],
    },
  );
  const hasChecked = useRef(false);

  useEffect(() => {
    // Skip if already cached or checked in this component instance
    if (cachedStatus || hasChecked.current) {
      if (cachedStatus) {
        setStatus(cachedStatus);
      }
      return;
    }

    // Skip startup checks in production unless explicitly enabled
    if (!import.meta.env.DEV && !import.meta.env.VITE_STARTUP_CHECK_ENABLED) {
      const skipStatus = {
        supabaseConnected: true,
        edgeFunctionsAvailable: true,
        isChecking: false,
        errors: [],
      };
      setStatus(skipStatus);
      cachedStatus = skipStatus;
      return;
    }

    hasChecked.current = true;

    const checkStartupRequirements = async () => {
      const errors: string[] = [];

      // Check Supabase connection with timeout
      try {
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('timeout')), 2000),
        );

        const sessionPromise = supabase.auth.getSession();
        const { error } = await Promise.race([sessionPromise, timeoutPromise]);

        if (error && error.message.includes('connection')) {
          errors.push('Supabase is not connected. Run: supabase start');
          if (import.meta.env.DEV) {
            logger.error('Supabase connection error:', error);
          }
        }
      } catch (e) {
        if (e.message === 'timeout') {
          // Skip error for timeout to prevent blocking the UI
        } else {
          errors.push('Cannot connect to Supabase');
        }
      }

      // Check Edge Functions with timeout
      let edgeFunctionsAvailable = false;
      try {
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('timeout')), 1000),
        );

        const edgeCheckPromise = checkEdgeFunctionsStatus();
        edgeFunctionsAvailable = await Promise.race([edgeCheckPromise, timeoutPromise]);
      } catch {
        // Edge functions check failed or timed out - don't block the UI
        edgeFunctionsAvailable = false;
      }

      if (!edgeFunctionsAvailable && import.meta.env.DEV) {
        errors.push(
          'Edge Functions are not running. Run: supabase functions serve --no-verify-jwt',
        );
      }

      // Update status and cache it
      const newStatus = {
        supabaseConnected: errors.length === 0,
        edgeFunctionsAvailable,
        isChecking: false,
        errors,
      };
      setStatus(newStatus);
      cachedStatus = newStatus;

      // Show errors only in development and only once per session
      if (
        errors.length > 0 &&
        import.meta.env.DEV &&
        !sessionStorage.getItem('startup-errors-shown')
      ) {
        sessionStorage.setItem('startup-errors-shown', 'true');
        // Show max 2 errors to avoid spam
        errors.slice(0, 2).forEach((error) => {
          showErrorToast(error);
        });
      }
    };

    setTimeout(() => {
      setTimeout(() => {
        checkStartupRequirements();
      }, 3000);
    }, 0);
  }, []);

  return status;
}
